import { useState } from "react";

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";

import { useCachedFirebaseCrud } from "../../../helpers/firebase";

const sortByUpdated = (arr) =>
  [...arr].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

const buildConversationPreview = (conv) => {
  if (conv?.context === "emissions-survey") {
    return conv.surveyYear
      ? `Emissions Survey — ${conv.surveyYear}`
      : "Emissions Survey";
  }
  const firstUserTurn = (conv?.turns || []).find((t) => t.role === "user");
  return firstUserTurn?.content?.slice(0, 80) || "New conversation";
};

const ConvCard = ({
  conv,
  isSelected,
  onSelect,
  onAction,
  actionIcon,
  actionLabel
}) => {
  const preview = buildConversationPreview(conv);
  const date = conv.updatedAt
    ? new Date(conv.updatedAt).toLocaleDateString()
    : "";

  return (
    <ListItem disablePadding>
      <Paper
        elevation={isSelected ? 4 : 2}
        style={{ width: "100%", borderRadius: "8px" }}
      >
        <Box display="flex" alignItems="center">
          <ListItemButton
            selected={isSelected}
            onClick={() => onSelect(conv.id)}
            style={{ borderRadius: "8px", flexGrow: 1 }}
          >
            <ListItemText primary={preview} secondary={date} />
            <Tooltip title={actionLabel}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  return onAction(conv.id);
                }}
              >
                {actionIcon}
              </IconButton>
            </Tooltip>
          </ListItemButton>
        </Box>
      </Paper>
    </ListItem>
  );
};

const ConversationHistoryPanel = ({
  conversations,
  conversationsLoading,
  selectedId,
  onSelect,
  onNew
}) => {
  const [showArchived, setShowArchived] = useState(false);
  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const active = (conversations || []).filter((c) => !c.archived);
  const archived = (conversations || []).filter((c) => c.archived);

  const handleArchive = (id) =>
    updateCollectionDoc("agent-conversations", id, { archived: true });
  const handleUnarchive = (id) =>
    updateCollectionDoc("agent-conversations", id, { archived: false });

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      <Button
        fullWidth
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onNew}
      >
        New Chat
      </Button>
      {conversationsLoading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress size={20} />
        </Box>
      )}
      {!conversationsLoading && active.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No previous conversations yet.
        </Typography>
      )}
      {!conversationsLoading && active.length > 0 && (
        <List
          dense
          disablePadding
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          {sortByUpdated(active).map((conv) => (
            <ConvCard
              key={conv.id}
              conv={conv}
              isSelected={conv.id === selectedId}
              onSelect={onSelect}
              onAction={handleArchive}
              actionIcon={<ArchiveIcon fontSize="small" />}
              actionLabel="Archive"
            />
          ))}
        </List>
      )}
      {archived.length > 0 && (
        <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
          <Button
            size="small"
            color="primary"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived
              ? "Hide archived"
              : `Show archived (${archived.length})`}
          </Button>
          <Collapse in={showArchived}>
            <List
              dense
              disablePadding
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            >
              {sortByUpdated(archived).map((conv) => (
                <ConvCard
                  key={conv.id}
                  conv={conv}
                  isSelected={conv.id === selectedId}
                  onSelect={onSelect}
                  onAction={handleUnarchive}
                  actionIcon={<UnarchiveIcon fontSize="small" />}
                  actionLabel="Unarchive"
                />
              ))}
            </List>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default ConversationHistoryPanel;
